import { IPatient } from '../../../../constants/types';
import { yearsBetween } from '../../../../utils/birthdate';
import { SourceState } from '../types';

/**
 * SHER-896. One patient of the demographics population, as the panel needs them: an age and a
 * sex, and nothing else.
 *
 * Deliberately NOT the decrypted `IPersonalData` record. The panel reads two of its eight
 * fields, so threading the whole thing through would hand the chart layer names, surnames and
 * phone numbers it has no use for — and would make the components responsible for parsing
 * birthdates and appointment dates, which is a data-boundary job.
 *
 * `age` is `null` for a patient whose birthdate cannot be read OR whose first appointment the
 * endpoint did not report. Both are the same statement — "we cannot say how old they were" —
 * and both land in the same Unknown bucket. An earlier version defaulted a missing appointment
 * date to today, which quietly made one histogram mix two different measurements: age at the
 * visit for most patients and age now for the rest, with nothing on screen to tell them apart.
 */
export interface DemographicPatient {
  age: number | null;
  sex?: string;
}

/**
 * The projection, at the boundary where the population meets the decrypted patients.
 *
 * Takes the ids and the reference dates rather than a response, because two endpoints answer
 * this question with different field names — outpatients by first appointment, inpatients by
 * first admission — and the panel does not care which. SHER-897 widened it from the outpatient
 * response shape rather than forking a second copy.
 *
 * Exported and named rather than inlined into a hook so the ticket's central behaviour — age at
 * the patient's own reference date — keeps a test it can fail. Hiding it in an untested
 * `select` is how a guard loses its subject while staying green.
 */
export const toDemographicPatients = (
  ids: number[] | undefined,
  referenceDates: Record<number, string> | undefined,
  patients: IPatient[],
): DemographicPatient[] => {
  const population = ids ?? [];
  const dates = referenceDates ?? {};

  return patients
    .filter((patient) => population.includes(patient.id))
    .map((patient) => ({
      // `yearsBetween` returns null when either date is unreadable, so a missing reference date
      // needs no branch of its own here.
      age: yearsBetween(patient.personalData?.birthdate, dates[patient.id] ?? null),
      sex: patient.personalData?.sex,
    }));
};

/**
 * What state the demographics panel is in, given its two inputs.
 *
 * They land independently: the endpoint's id list, and the patients Redux decrypts in the
 * background. Judging the card on the response alone let it announce "no outpatients in this
 * period" mid-decrypt and then change its mind — not a slow card, a card stating something
 * false.
 *
 * Returns the whole `SourceState`, not a boolean, so the decision and the consequence stay in
 * one place. The hook's reducer previously owned the pending-vs-error choice and the failure
 * message, which meant a generic loop held knowledge about this one endpoint.
 *
 * `settled` is what separates "not here yet" from "not coming". Without it a failed decrypt
 * parked the card on a skeleton for the life of the mount, which is worse than the wrong
 * message it replaced because it never ends.
 *
 * The `Error` deliberately carries no message: `ErrorState` falls back to its own copy, and
 * manufacturing an English sentence here would print it verbatim to a French tenant.
 */
export const demographicsState = (
  ids: number[] | undefined,
  patients: IPatient[],
  settled: boolean,
): SourceState => {
  // `undefined` and `[]` are different answers and must not collapse. `[]` is "the endpoint
  // named zero patients" — a quiet period, which the card may report. `undefined` is "the
  // endpoint named no population at all", which is a response the card cannot interpret: a
  // rejected request, or an API older than this client. Reading the second as the first is how
  // the panel came to announce "no patients in the selected period" over a response that never
  // said anything of the kind.
  if (ids === undefined) return { status: 'error', error: new Error() };

  // Both halves are load-bearing. "No patients decrypted" alone would park a tenant with
  // genuinely no patients in the period on a skeleton for ever: it is never going to decrypt
  // anyone.
  const haveWhatWeNeed = patients.length > 0 || ids.length === 0;
  if (haveWhatWeNeed) return { status: 'ok' };
  return settled ? { status: 'error', error: new Error() } : { status: 'pending' };
};

/**
 * The buckets, declared once and consumed by both the chart and its tests.
 *
 * They partition: each bucket's `max` is the last age it contains and the next one starts at
 * `max + 1`. The version this replaces tested `age <= 75` for the "61-75" bucket and sent
 * everything else to a bucket labelled "75+", so age 75 landed in the first and the label on
 * the second was a lie about its own contents.
 */
export const AGE_BUCKETS = [
  { id: '0-17', max: 17 },
  { id: '18-30', max: 30 },
  { id: '31-45', max: 45 },
  { id: '46-60', max: 60 },
  { id: '61-74', max: 74 },
  // Infinity rather than null, so `find` is total and no caller needs a non-null assertion to
  // say what the type already guarantees.
  { id: '75+', max: Infinity },
] as const;

/** Patients whose age could not be established. A bucket, not a silent drop. */
export const UNKNOWN_AGE_BUCKET = 'unknown';

// The ids are also the axis labels: the six ranges read the same to a user as to the code, and
// only the unknown row needs translating. A parallel `label` field held the identical string in
// all six rows and the chart mapped it to itself.
export type AgeBucketId = typeof AGE_BUCKETS[number]['id'] | typeof UNKNOWN_AGE_BUCKET;
export type SexKey = typeof SEX_MALE | typeof SEX_FEMALE | typeof SEX_UNKNOWN;

export const SEX_MALE = 'Male';
export const SEX_FEMALE = 'Female';
/** Anything that is neither, including missing. */
export const SEX_UNKNOWN = 'Unknown';

/**
 * Counts per bucket, always including every bucket and `unknown` — a bucket missing from the
 * result renders as a gap in the axis rather than as a zero.
 */
export const bucketAges = (patients: DemographicPatient[]): Record<AgeBucketId, number> => {
  const counts = { [UNKNOWN_AGE_BUCKET]: 0 } as Record<AgeBucketId, number>;
  AGE_BUCKETS.forEach((bucket) => { counts[bucket.id] = 0; });

  patients.forEach(({ age }) => {
    if (age === null) {
      counts[UNKNOWN_AGE_BUCKET] += 1;
      return;
    }
    // Total by construction: the last bucket's max is Infinity.
    const bucket = AGE_BUCKETS.find((candidate) => age <= candidate.max) ?? AGE_BUCKETS[AGE_BUCKETS.length - 1];
    counts[bucket.id] += 1;
  });

  return counts;
};

/**
 * Male / Female / Unknown. The version this replaces read only the first two and plotted them
 * against a total that was also only the first two, so a patient recorded as anything else
 * vanished from the chart AND from the denominator — the percentages summed to 100 while
 * describing fewer patients than the card claimed.
 */
export const sexDistribution = (patients: DemographicPatient[]): Record<SexKey, number> => {
  const counts: Record<SexKey, number> = { [SEX_MALE]: 0, [SEX_FEMALE]: 0, [SEX_UNKNOWN]: 0 };

  patients.forEach(({ sex }) => {
    if (sex === SEX_MALE || sex === SEX_FEMALE) counts[sex] += 1;
    else counts[SEX_UNKNOWN] += 1;
  });

  return counts;
};

/**
 * Mean age over the patients whose age is known. `null` when none is.
 *
 * Patients with an unknown age are excluded from the mean rather than counted as 0 — counting
 * them as 0 is what the old code did, and it dragged the average towards zero in proportion to
 * how much data was missing.
 */
export const averageAge = (patients: DemographicPatient[]): number | null => {
  const ages = patients
    .map(({ age }) => age)
    .filter((age): age is number => age !== null);
  if (!ages.length) return null;
  return ages.reduce((total, age) => total + age, 0) / ages.length;
};
